import React, { useState, useCallback } from 'react';

import { useQuery } from '@apollo/client';
import { Language, Phone, Add, Edit } from '@mui/icons-material';
import { Button, Typography } from '@mui/material';
import { Navigate, useParams } from 'react-router-dom';

import RouteLink from '@ivy/components/atoms/RouteLink';
import ReviewSnippet from '@ivy/components/molecules/ReviewSnippet';
import EditEmployerProfilePopup from '@ivy/components/organisms/EditEmployerProfilePopup';
import ReviewPopup from '@ivy/components/organisms/ReviewPopup';
import SalaryReportPopup from '@ivy/components/organisms/SalaryReportPopup';
import TabTemplate, { IconTypes } from '@ivy/components/templates/TabTemplate';
import {
	type InfoSnippetProps,
	type PageTabProps,
} from '@ivy/components/templates/TabTemplate/TabTemplate';
import { Profession, PROFESSION2SLUG } from '@ivy/constants/clinician';
import { ORGNIZATION_PLACEHOLDER_IMAGE } from '@ivy/constants/organization';
import { RouteUri } from '@ivy/constants/routes';
import { useCurrentAccount } from '@ivy/gql/hooks';
import { gql } from '@ivy/gql/types';
import { type OrgShow_OrgQuery } from '@ivy/gql/types/graphql';
import { formatAddress } from '@ivy/lib/formatting/location';
import { formatInteger } from '@ivy/lib/formatting/number';
import { convertUriToHttpsUrl } from '@ivy/lib/formatting/url';
import { buildInternalLink, getRelativeRoute } from '@ivy/lib/util/route';
import { formatPhoneNumber } from '@ivy/lib/validation/phone';

import OrgFacilityList from './OrgFacilityList';
import OrgProfile from './OrgProfile';
import OrgReviews from './OrgReviews';
import OrgSalary from './OrgSalary';

const OrgShow_OrgQDoc = gql(/* GraphQL */ `
	query OrgShow_Org($orgId: uuid!) {
		org: organization_by_pk(id: $orgId) {
			id
			description
			logo {
				id
				publicUrl: public_url
			}
			name
			streetAddress1: street_address_1
			streetAddress2: street_address_2
			city
			state
			zipcode
			phone
			website
			slug
			contractsAgg: contracts_aggregate(where: { active: { _eq: true } }) {
				aggregate {
					count
				}
			}
			surveys: acep_org_surveys(
				where: { accepted_at: { _is_null: false } }
				order_by: [{ accepted_at: desc_nulls_last }]
				limit: 1
			) {
				id
				orgName: org_name
			}
			reviewsAgg: reviews_aggregate(
				where: { active: { _eq: true }, visible: { _eq: true } }
			) {
				aggregate {
					avg {
						rating: rating_org
					}
					count
				}
			}
			salaryAgg: salary_reports_fmt_aggregate {
				aggregate {
					count
				}
			}
			...SalaryReportPopup_Organization
			...ReviewPopup_Organization
		}
	}
`);

export interface OrgShowProps {
	showAcepSurvey?: boolean;
	showAcepSurveyCta?: boolean;
	showAcepSurveyDisclaimer?: boolean;
	showDescription?: boolean;
	showWhitelabelCta?: boolean;
	showFacilitiesTab?: boolean;
	showSurveyName?: boolean;
	showReviewsTab?: boolean;
	showSalariesTab?: boolean;
	professions?: Profession[];
	getPageDescription?: (org: NonNullable<OrgShow_OrgQuery['org']>) => string;
	pageNoIndex?: boolean;
}

const OrgShow = ({
	getPageDescription,
	showFacilitiesTab,
	showSurveyName,
	showReviewsTab,
	showSalariesTab,
	pageNoIndex,
	...props
}: OrgShowProps) => {
	const [showSalaryReport, setShowSalaryReport] = useState(false);
	const [showReview, setShowReview] = useState(false);
	const [showEditProfile, setShowEditProfile] = useState(false);
	const { orgId: rawOrgId } = useParams();
	const currAcc = useCurrentAccount();
	const orgId =
		rawOrgId?.toLowerCase() === 'current'
			? currAcc?.orgUser?.org.id || ''
			: rawOrgId?.substring(0, 36);
	const isClinicianOrAnon = !currAcc || currAcc?.isClinician;
	const { data, loading, error } = useQuery(OrgShow_OrgQDoc, {
		variables: {
			orgId,
		},
		skip: !orgId,
	});

	const orgName =
		(showSurveyName && data?.org?.surveys[0]?.orgName) || data?.org?.name;

	const handleCloseSalaryReport = useCallback(() => {
		setShowSalaryReport(false);
	}, [setShowSalaryReport]);

	const handleOpenSalaryReport = useCallback(() => {
		setShowSalaryReport(true);
	}, [setShowSalaryReport]);

	const handleCloseReview = useCallback(() => {
		setShowReview(false);
	}, [setShowReview]);

	const handleOpenReview = useCallback(() => {
		setShowReview(true);
	}, [setShowReview]);

	const handleCloseEditProfile = useCallback(() => {
		setShowEditProfile(false);
	}, [setShowEditProfile]);

	const handleOpenEditProfile = useCallback(() => {
		setShowEditProfile(true);
	}, [setShowEditProfile]);

	if (!orgId) {
		// Replace so they don't attempt to come back to this page
		return <Navigate to={RouteUri.ROOT} replace />;
	}

	return (
		<React.Fragment>
			<TabTemplate
				title={orgName || ''}
				// Use an array to nest the page title
				pageTitle={orgName && [orgName]}
				showSupportLauncher
				pageDescription={
					data?.org && getPageDescription
						? getPageDescription(data.org)
						: undefined
				}
				image={data?.org?.logo?.publicUrl || ORGNIZATION_PLACEHOLDER_IMAGE}
				middleInfo={
					showReviewsTab ? (
						<ReviewSnippet
							rating={data?.org?.reviewsAgg.aggregate?.avg?.rating}
							count={data?.org?.reviewsAgg.aggregate?.count}
							sx={{ minWidth: '140px' }}
							redirect={
								data?.org
									? buildInternalLink(RouteUri.ORG_REVIEW_LIST, {
											orgId: [data.org.id, data.org.slug],
									  })
									: undefined
							}
						/>
					) : undefined
				}
				infoArray={
					[
						data?.org?.streetAddress1
							? {
									icon: IconTypes.LOCATION,
									text: formatAddress(
										data?.org?.streetAddress1,
										data?.org?.streetAddress2,
										data?.org?.city,
										data?.org?.state,
										data?.org?.zipcode,
									),
							  }
							: null,
						data?.org?.website
							? {
									icon: <Language />,
									text: (
										<RouteLink
											to={convertUriToHttpsUrl(data.org.website)}
											openInNewTab
											underline='hover'
										>
											{data.org.website
												.replace('http://', '')
												.replace('https://', '')}
										</RouteLink>
									),
							  }
							: null,
						data?.org?.phone
							? {
									icon: <Phone />,
									text: formatPhoneNumber(data.org.phone),
							  }
							: null,
					].filter((el) => el) as InfoSnippetProps[]
				}
				tabs={
					data?.org
						? ([
								{
									id: 'OrgShow_OverviewTab',
									path: getRelativeRoute(RouteUri.ORG_SHOW, RouteUri.ORG_INDEX),
									index: true,
									label: 'Overview',
									element: <OrgProfile orgId={data.org.id} {...props} />,
									NavTemplateProps: {
										pathIsCanonical: buildInternalLink(RouteUri.ORG_SHOW, {
											orgId: [data.org.id, data.org.slug],
										}),
										pageNoIndex,
									},
									action:
										currAcc?.isOrgUser &&
										(orgId === 'current' ||
											orgId === currAcc.orgUser?.org.id) ? (
											<Button
												id='OrgShow_EditProfileBtn'
												variant='contained'
												startIcon={<Edit />}
												onClick={handleOpenEditProfile}
												sx={{
													px: { md: 3, xs: 1 },
													py: { md: 2, xs: 1 },
													my: 'auto',
													flex: { md: 'none', xs: 1 },
												}}
											>
												<Typography variant='body1' fontWeight='bold'>
													Edit Profile
												</Typography>
											</Button>
										) : undefined,
								},
								showFacilitiesTab && data?.org?.contractsAgg.aggregate?.count
									? {
											id: 'OrgShow_FacilitiesJobsTab',
											path: getRelativeRoute(
												RouteUri.ORG_FACILITY_LIST_PAGE + '?',
												RouteUri.ORG_INDEX,
											),
											link: getRelativeRoute(
												buildInternalLink(RouteUri.ORG_FACILITY_LIST, {
													profession:
														PROFESSION2SLUG[
															currAcc?.clinician?.profession ||
																Profession.PHYSICIAN
														],
													slug: 'search',
												}),
												RouteUri.ORG_INDEX,
											),
											label: `Facilities & Jobs (${formatInteger(
												data.org.contractsAgg.aggregate.count,
											)})`,
											hideInfoArray: true,
											element: <OrgFacilityList orgId={data.org.id} />,
											NavTemplateProps: {
												// Use an array to nest the page title
												pageTitle: ['Facilities & Jobs'],
												fullscreen: true,
												showFooter: false,
												// TODO: enable indexing this page
												pageNoIndex: true,
												showSupportLauncher: false,
											},
											action:
												currAcc?.isOrgUser &&
												(orgId === 'current' ||
													orgId === currAcc.orgUser?.org.id) ? (
													<Button
														id='OrgShow_ManageFacilitiesBtn'
														component={RouteLink}
														to={RouteLink.routes.MANAGEMENT_FACILITY}
														variant='contained'
														sx={{
															px: { md: 3, xs: 1 },
															py: { md: 2, xs: 1 },
															my: 'auto',
															flex: { md: 'none', xs: 1 },
														}}
													>
														<Typography variant='body1' fontWeight='bold'>
															Manage Facilities
														</Typography>
													</Button>
												) : undefined,
									  }
									: null,
								showReviewsTab
									? {
											path: getRelativeRoute(
												RouteUri.ORG_REVIEW_LIST,
												RouteUri.ORG_INDEX,
											),
											label: `Reviews (${formatInteger(
												data?.org?.reviewsAgg?.aggregate?.count || 0,
											)})`,
											element: orgId ? <OrgReviews orgId={orgId} /> : null,
											NavTemplateProps: {
												// Use an array to nest the page title
												pageTitle: ['Reviews'],
												fullscreen: false,
												showFooter: true,
												pathIsCanonical: buildInternalLink(
													RouteUri.ORG_REVIEW_LIST,
													{
														orgId: [data.org.id, data.org.slug],
													},
												),
												// Don't index if no reviews
												pageNoIndex:
													pageNoIndex || !data.org.reviewsAgg.aggregate?.count,
											},
											action: isClinicianOrAnon ? (
												<Button
													variant='contained'
													startIcon={<Add />}
													onClick={handleOpenReview}
													sx={{
														px: { md: 3, xs: 1 },
														py: { md: 2, xs: 1 },
														my: 'auto',
														flex: { md: 'none', xs: 1 },
													}}
												>
													<Typography variant='body1' fontWeight='bold'>
														Add review
													</Typography>
												</Button>
											) : undefined,
									  }
									: null,
								showSalariesTab
									? {
											path: getRelativeRoute(
												RouteUri.ORG_SALARY_LIST,
												RouteUri.ORG_INDEX,
											),
											label: `Salaries (${formatInteger(
												data?.org?.salaryAgg?.aggregate?.count || 0,
											)})`,
											element: orgId ? (
												<OrgSalary
													orgId={orgId}
													onClickSalary={handleOpenSalaryReport}
												/>
											) : null,
											NavTemplateProps: {
												// Use an array to nest the page title
												pageTitle: ['Salaries'],
												fullscreen: false,
												showFooter: true,
												pathIsCanonical: buildInternalLink(
													RouteUri.ORG_SALARY_LIST,
													{
														orgId: [data.org.id, data.org.slug],
													},
												),
												// Don't index if no salaries
												pageNoIndex:
													pageNoIndex || !data.org.salaryAgg.aggregate?.count,
											},
											action: isClinicianOrAnon ? (
												<Button
													variant='contained'
													startIcon={<Add />}
													onClick={handleOpenSalaryReport}
													sx={{
														px: { md: 3, xs: 1 },
														py: { md: 2, xs: 1 },
														my: 'auto',
														flex: { md: 'none', xs: 1 },
														display: { gridBreak: 'none', xs: 'inline-flex' },
													}}
												>
													<Typography variant='body1' fontWeight='bold'>
														Add Salary
													</Typography>
												</Button>
											) : undefined,
									  }
									: null,
						  ].filter((el) => el) as PageTabProps[])
						: []
				}
				DataLoaderProps={{
					data,
					loading,
					error,
					keys: ['org'],
				}}
			/>
			{showSalaryReport && (
				<SalaryReportPopup
					open
					organization={data!.org!}
					onClose={handleCloseSalaryReport}
					refetchQueries={[
						'OrgShow_Org',
						'OrgSalary_Org',
						'SalaryList_SearchSalaryReports',
					]}
				/>
			)}
			{showReview && (
				<ReviewPopup
					open
					organization={data!.org!}
					onSuccess={handleCloseReview}
					onClose={handleCloseReview}
					refetchQueries={[
						'OrgShow_Org',
						'OrgReviews_Org',
						'ReviewList_SearchReview',
					]}
				/>
			)}
			{showEditProfile && (
				<EditEmployerProfilePopup
					open
					onSuccess={handleCloseEditProfile}
					onClose={handleCloseEditProfile}
					refetchQueries={['OrgShow_Org']}
				/>
			)}
		</React.Fragment>
	);
};

export default OrgShow;
